/*
 * Copyright (c) 2020 Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";

import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";

import { FormTextField, submitExButton } from "components/Form/FormField";
import { sRightAns } from "assets/jss/views/ResStrings";
import {
  TestActionParams,
  TestResult,
  reportCorrect,
  reportWrong,
  reportFeedback,
  showHtmlText,
} from "./ExMain";
import exerciseStyle from "assets/jss/views/exercise/exerciseStyle.jsx";
const styles = createStyles(exerciseStyle);

interface Props extends WithStyles<typeof styles> {
  classes: any;
  exercise: any;
  testResult: TestResult;
  quit: boolean;
  onAction: (params: TestActionParams) => void;
}

const ExFillBase: React.FC<Props> = ({
  classes,
  exercise,
  testResult,
  quit,
  onAction,
}: Props) => {
  const [s_report, setReport] = useState<React.ReactNode>(null);

  useEffect(() => {
    if (testResult) {
      setReport(fillTextReport(testResult.userAnsStr || ""));
    }
  }, [exercise]);

  useEffect(() => {
    if (quit) {
      onAction({ action: "quit", data: {} });
    }
  }, [quit]);

  const renderTestBody = () => {
    const style = { marginBottom: "0.5rem" };
    return (
      <div style={{ display: "flex", alignItems: "flex-end" }}>
        <span style={style}>{showHtmlText(exercise.aux[0])}</span>&nbsp;&nbsp;
        <Field
          name={`userAns`}
          component={FormTextField}
          color={"secondary"}
          disabled={testResult ? true : false}
        />
        <span style={style}>{showHtmlText(exercise.aux[1])}</span>
      </div>
    );
  };

  const checkAns = (userAns: string) => {
    // compare all possible ansers, in lowercase
    const userAnsL = userAns.toLowerCase();
    return exercise.e_text.findIndex(
      (ans: string) => ans.toLowerCase() === userAnsL,
    );
  };

  const fillTextReport = (userAns: string) => {
    const ansIdx = checkAns(userAns);
    const isCorrect = ansIdx !== -1;

    // feedback display rule for Fill-a-Blank
    // if user's answer is wrong, show the 1st (0th) feedback;
    // if user's answer is correct, show the feedback linked with the answer
    let feedbk: React.ReactNode = null;
    if (!isCorrect && exercise.e_feedback[0] !== "")
      feedbk = showHtmlText(exercise.e_feedback[0], classes);
    if (isCorrect && exercise.e_feedback[ansIdx] !== "")
      feedbk = showHtmlText(exercise.e_feedback[ansIdx], classes);

    return (
      <>
        {isCorrect
          ? reportCorrect(classes)
          : reportWrong(classes, userAns === "")}
        {!isCorrect && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className={classes.reportCorrect}>{sRightAns}&nbsp;</div>
            <u>{showHtmlText(exercise.e_text[0], classes)}</u>
          </div>
        )}
        {(feedbk !== null || exercise.explain !== "") &&
          reportFeedback(feedbk, exercise.explain, exercise.fbk, classes)}
      </>
    );
  };

  return (
    <div className={classes.exContainer}>
      {showHtmlText(exercise.statement, classes)}
      <Formik
        initialValues={{
          userAns:
            testResult && testResult.userAnsStr ? testResult.userAnsStr : "",
        }}
        enableReinitialize={true}
        onSubmit={(values, formikBag) => {
          formikBag.resetForm({ userAns: "" });
          onAction({
            action: "next",
            data: {
              userAnsStr: values.userAns,
              correct: checkAns(values.userAns) !== -1,
            },
          });
        }}
        render={({ isValid, isSubmitting }) => {
          return (
            <Form>
              {renderTestBody()}
              {(!testResult && submitExButton("Next", !isValid)) ||
                isSubmitting}
              {s_report}
            </Form>
          );
        }}
      />
    </div>
  );
};

export const ExFill = withStyles(styles)(ExFillBase);
